// components/user/bank-account/BankEditForm.jsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Card } from 'antd';
import { 
  UserOutlined, 
  BankFilled, 
  CreditCardOutlined, 
  QrcodeOutlined, 
  SaveOutlined, 
  CloseOutlined 
} from '@ant-design/icons';
import { useFormik } from 'formik';
import { updateBankDetails } from '../../../store/slices/profileSlice';
import { bankValidationSchema } from '../../../validations/profileValidation';
import toast from 'react-hot-toast';

const BankEditForm = ({ onCancel, isDarkMode }) => {
  const dispatch = useDispatch();
  const { user, updatingBank, error } = useSelector((state) => state.profile);
  const bankDetails = user?.bank_details;

  const cleanValue = (value) => {
    if (!value || value === 'null' || value === 'undefined') {
      return '';
    }
    return value;
  };

  const formik = useFormik({
    initialValues: {
      banking_name: cleanValue(bankDetails?.banking_name),
      bank_name: cleanValue(bankDetails?.bank_name),
      account_no: cleanValue(bankDetails?.account_no),
      ifsc_code: cleanValue(bankDetails?.ifsc_code),
      branch: cleanValue(bankDetails?.branch),
      upi_id: cleanValue(bankDetails?.upi_id),
    },
    validationSchema: bankValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result = await dispatch(updateBankDetails(values));
      if (updateBankDetails.fulfilled.match(result)) {
        toast.success("Bank details updated successfully");
        onCancel();
      } else if (updateBankDetails.rejected.match(result)) {
        toast.error(result.payload || "Failed to update bank details");
      }
    },
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      // Clear error after showing
      // You can add clearProfileError action if needed
    }
  }, [error]);

  return (
    <Card className="border-0 shadow-sm rounded-xl">
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {bankDetails?.bank_name && bankDetails?.bank_name !== 'null' ? 'Edit Bank Details' : 'Add Bank Details'}
        </h3>
      </div>

      <form onSubmit={formik.handleSubmit} className="pt-2">
        <div className="mb-4">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Account Holder Name *
          </label>
          <Input
            name="banking_name"
            placeholder="Enter name as per bank account"
            value={formik.values.banking_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<UserOutlined className="text-xs text-gray-400" />}
            className="text-sm rounded-lg"
            size="middle"
            status={formik.touched.banking_name && formik.errors.banking_name ? "error" : ""}
          />
          {formik.touched.banking_name && formik.errors.banking_name && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.banking_name}</div>
          )}
        </div>

        <div className="mb-4">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Bank Name *
          </label>
          <Input
            name="bank_name"
            placeholder="Enter bank name"
            value={formik.values.bank_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<BankFilled className="text-xs text-gray-400" />}
            className="text-sm rounded-lg"
            size="middle"
            status={formik.touched.bank_name && formik.errors.bank_name ? "error" : ""}
          />
          {formik.touched.bank_name && formik.errors.bank_name && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.bank_name}</div>
          )}
        </div>

        <div className="mb-4">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Account Number *
          </label>
          <Input
            name="account_no"
            placeholder="Enter account number"
            value={formik.values.account_no}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<CreditCardOutlined className="text-xs text-gray-400" />}
            className="text-sm rounded-lg"
            size="middle"
            status={formik.touched.account_no && formik.errors.account_no ? "error" : ""}
          />
          {formik.touched.account_no && formik.errors.account_no && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.account_no}</div>
          )}
        </div>

        <div className="mb-4">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            IFSC Code *
          </label>
          <Input
            name="ifsc_code"
            placeholder="Enter IFSC code (e.g., SBIN0001234)"
            value={formik.values.ifsc_code?.toUpperCase()}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              formik.setFieldValue('ifsc_code', value);
            }}
            onBlur={formik.handleBlur}
            className="text-sm uppercase rounded-lg"
            size="middle"
            maxLength={11}
            status={formik.touched.ifsc_code && formik.errors.ifsc_code ? "error" : ""}
          />
          {formik.touched.ifsc_code && formik.errors.ifsc_code && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.ifsc_code}</div>
          )}
        </div>

        <div className="mb-4">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Branch *
          </label>
          <Input
            name="branch"
            placeholder="Enter branch name"
            value={formik.values.branch}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="text-sm rounded-lg"
            size="middle"
            status={formik.touched.branch && formik.errors.branch ? "error" : ""}
          />
          {formik.touched.branch && formik.errors.branch && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.branch}</div>
          )}
        </div>

        <div className="mb-6">
          <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            UPI ID <span className="text-gray-400">(Optional)</span>
          </label>
          <Input
            name="upi_id"
            placeholder="Enter UPI ID (e.g., username@okhdfcbank)"
            value={formik.values.upi_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<QrcodeOutlined className="text-xs text-gray-400" />}
            className="text-sm rounded-lg"
            size="middle"
            status={formik.touched.upi_id && formik.errors.upi_id ? "error" : ""}
          />
          {formik.touched.upi_id && formik.errors.upi_id && (
            <div className="mt-1 text-xs text-red-500">{formik.errors.upi_id}</div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            icon={<CloseOutlined />}
            size="middle"
            className="flex-1"
            disabled={updatingBank}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updatingBank}
            icon={<SaveOutlined />}
            size="middle"
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            Save Details
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default BankEditForm;